import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import { forkJoin } from 'rxjs';

import { Tools } from '../../core/utils/tools';
import { CurrentShareholderGeneralMeetingModel } from '../../models/current-shareholder-general-meeting.model';
import { RegistrationStatusEnum } from '../../models/enums/registration-status.enum';
import { GeneralMeetingModel } from '../../models/general-meeting.model';
import { ShareholderInstructionsSummaryModel } from '../../models/shareholder-instructions-summary.model';
import { OptionalType } from '../../models/types/optional.type';
import { BasicDataService } from '../../services/basic-data.service';
import { GeneralMeetingsResource } from '../../services/resources/general-meetings.resource';
import { MetaResource } from '../../services/resources/meta.resource';
import { AppSessionStorage } from '../../services/storage/app.session-storage';

@Component({
    selector: 'sl-general-meeting-registration-print',
    standalone: false,
    templateUrl: './general-meeting-registration-print.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './general-meeting-registration-print.component.scss'
})
export class GeneralMeetingRegistrationPrintComponent implements OnInit {
    readonly RegistrationStatusEnum = RegistrationStatusEnum;

    readonly isLoading = signal(false);
    readonly instructions = signal<OptionalType<ShareholderInstructionsSummaryModel>>(undefined);
    readonly shouldNetvoteUseWordingForCooperatives = signal(false);
    readonly currentMeeting = signal<OptionalType<GeneralMeetingModel>>(undefined);
    readonly currentShareholderData = signal<OptionalType<CurrentShareholderGeneralMeetingModel>>(undefined);
    readonly tenantLogo = signal('');
    readonly hasPresetInstructions = signal(false);
    readonly instructionsText = signal('');

    private readonly router = inject(Router);
    private readonly generalMeetingsResource = inject(GeneralMeetingsResource);
    private readonly basicDataService = inject(BasicDataService);
    private readonly metaResource = inject(MetaResource);
    private readonly appSessionStorage = inject(AppSessionStorage); // TODO: Part of the temporary hack - to be removed.

    ngOnInit(): void {
        this.fetchData();
    }

    print(): void {
        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
        if (isSafari) {
            try {
                // Print for Safari browser
                document.execCommand('print', false);
            } catch {
                window.print();
            }
        } else {
            window.print();
        }
    }

    back(): void {
        this.router.navigateByUrl('/');
    }

    /**
     ***************************************************
     * TODO: TEMPORARY HACK - WILL BE REMOVED IN 4 WEEKS
     ***************************************************
     */
    get isMS(): boolean {
        return this.appSessionStorage.getCurrentContext() === 'multiplesklerose';
    }

    private fetchData(): void {
        this.isLoading.set(true);
        this.instructions.set(undefined);

        this.generalMeetingsResource.getCurrentForShareholder().subscribe(r => {
            if (r.value?.registrationStatus !== RegistrationStatusEnum.IndependentRepresentative) {
                // not allowed to be on this page. return to home
                this.router.navigateByUrl('/');
                return;
            }

            this.generalMeetingsResource.getCurrentInstructionsForShareholder().subscribe(ri => {
                this.instructions.set(ri.value);
                this.isLoading.set(false);
            });
        });

        const requests = forkJoin({
            basicData: this.basicDataService.get(),
            currentMeetingResponse: this.generalMeetingsResource.getCurrent(),
            currentShareholderResponse: this.generalMeetingsResource.getCurrentForShareholder(),
            currentInstructionsResponse: this.generalMeetingsResource.getCurrentInstructionsForShareholder()
        });

        requests
            .subscribe(r => {
                const registrationStatus = r.currentShareholderResponse.value?.registrationStatus;

                if (registrationStatus !== RegistrationStatusEnum.IndependentRepresentative) {
                    // not allowed to be on this page. return to home
                    this.router.navigateByUrl('/');
                    return;
                }

                const instructions = r.currentInstructionsResponse.value;
                if (!instructions || instructions.instructions.length === 0) {
                    // not allowed to be on this page. return to home
                    this.router.navigateByUrl('/');
                    return;
                }

                this.instructions.set(instructions);
                this.instructionsText.set(r.basicData.netvoteInstructionText);
                this.shouldNetvoteUseWordingForCooperatives.set(r.basicData.netvoteUseWordingForCooperatives);
                this.hasPresetInstructions.set(
                    r.basicData.netvoteShowAbstentionInstruction
                        || r.basicData.netvoteShowBodAgreeInstruction
                        || r.basicData.netvoteShowBodDisagreeInstruction
                        || r.basicData.netvoteShowNoInstruction
                        || r.basicData.netvoteShowYesInstruction
                );
                this.currentMeeting.set(r.currentMeetingResponse.value);
                this.currentShareholderData.set(r.currentShareholderResponse.value);
            })
            .add(() => this.isLoading.set(false));

        this.metaResource.getLogo().subscribe(resp => this.tenantLogo.set(Tools.Image.getImgSrcForBase64(resp.value)));
    }
}
